/**
 * labelPrintService — etiket yazıcılarına ham komut gönderimi.
 *
 * local-service üzerinden üç hedef desteklenir:
 *   devicePath  → USB doğrudan (usbprint.sys, üretici driver'ı gerekmez)
 *   tcp://host  → ağ yazıcısı, 9100
 *   printerName → Windows spooler (driver kurulu yazıcılar)
 *
 * Fiş yazdırmadan (localPrintService) ayrı tutuldu: orada sunucu bir fiş şeması
 * bekliyor ve iş kuyruğa alınıp poll ediliyor. Etiket işi ham byte gönderimi.
 */
import axios, { AxiosError } from 'axios';
import { LocalPrintError } from './localPrintService';
import { PrinterLanguage } from '../types/labelTemplate.types';

const DEFAULT_BASE_URL = 'http://localhost:3003';
const SELECTED_PRINTER_KEY = 'gts_label_printer';

export interface UsbPrinterInfo {
  devicePath: string;
  description: string;
  vendorId: string;
  productId: string;
}

export interface LabelPrinterSelection {
  /** USB doğrudan hedefi */
  devicePath?: string;
  /** Spooler adı veya tcp://host[:port] */
  printerName?: string;
  /** Kullanıcıya gösterilecek ad */
  label: string;
  /**
   * Yazıcının komut dili. Şablonda değil burada tutulur: aynı tasarım hem
   * tarayıcıdan hem farklı marka etiket yazıcılarından basılabilmeli.
   */
  language?: PrinterLanguage;
}

/** Ham gönderim için kodlayıcısı hazır olan diller */
export const RAW_SUPPORTED_LANGUAGES: PrinterLanguage[] = ['PPLA', 'PPLB', 'ZPL', 'EPL'];

/** Dil seçiminde gösterilecek açıklamalar */
export const LANGUAGE_LABELS: Record<PrinterLanguage, string> = {
  BROWSER: 'Tarayıcı (sürücü ile)',
  PPLA: 'PPLA — Argox (Datamax DPL)',
  PPLB: 'PPLB — Argox (Eltron EPL2)',
  ZPL: 'ZPL — Zebra, Argox PPLZ',
  EPL: 'EPL — Zebra (EPL2)',
};

export interface SendRawOptions {
  baseUrl?: string;
  timeoutMs?: number;
  jobName?: string;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000; // büyük dizide apply çağrı yığınını taşırır
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)) as any);
  }
  return btoa(binary);
}

function wrapError(err: unknown): LocalPrintError {
  const ax = err as AxiosError<any>;
  if (ax.response) {
    const data = ax.response.data || {};
    return new LocalPrintError(
      data.details || data.error || 'Etiket yazdırılamadı.',
      data.errorCode || 'PRINT_ERROR',
    );
  }
  if (ax.code === 'ECONNABORTED') {
    return new LocalPrintError('Etiket yazdırma zaman aşımına uğradı.', 'TIMEOUT');
  }
  return new LocalPrintError(
    'Yerel servise ulaşılamadı. AQUASOFT uygulamasının açık olduğundan emin olun.',
    'SERVICE_UNREACHABLE',
  );
}

/** Bağlı USB yazıcıları listeler (driver kurulu olması gerekmez) */
export async function listUsbPrinters(baseUrl = DEFAULT_BASE_URL): Promise<UsbPrinterInfo[]> {
  try {
    const resp = await axios.get(`${baseUrl}/api/usb-printers`, { timeout: 8000 });
    return Array.isArray(resp.data?.printers) ? resp.data.printers : [];
  } catch (err) {
    throw wrapError(err);
  }
}

export interface SystemPrinterInfo {
  /** Spooler adı veya "Etiket [tcp://host:port]" biçiminde ağ yazıcısı adı */
  name: string;
  /** Ağ yazıcısı — spooler ve driver devreye girmez, doğrudan TCP 9100 */
  isNetwork: boolean;
}

/**
 * Windows'a kurulu yazıcılar + tanımlı ağ yazıcıları.
 *
 * Fiş yazdırma ayarlarının kullandığı endpoint ile aynı; liste ikisini
 * birleştirip döndürüyor. Ağ yazıcıları adında `tcp://` taşıyor ve
 * local-service bu adı görünce spooler yerine soketten gönderiyor.
 */
export async function listSystemPrinters(baseUrl = DEFAULT_BASE_URL): Promise<SystemPrinterInfo[]> {
  try {
    const resp = await axios.get(`${baseUrl}/api/yazicilariGetir`, { timeout: 8000 });
    const list = Array.isArray(resp.data) ? resp.data : resp.data?.printers;
    if (!Array.isArray(list)) return [];

    return list
      .map((p: any) => {
        const name = typeof p === 'string' ? p : p?.name;
        if (!name) return null;
        return { name, isNetwork: p?.type === 'network' || /tcp:\/\//i.test(name) };
      })
      .filter((p): p is SystemPrinterInfo => p !== null);
  } catch {
    // Bu liste alınamasa da USB yolu çalışabilir — akışı kesme
    return [];
  }
}

/** Ham byte dizisini seçili yazıcıya gönderir */
export async function sendRawToPrinter(
  data: Uint8Array,
  target: LabelPrinterSelection,
  options: SendRawOptions = {},
): Promise<{ bytes: number; message: string }> {
  const { baseUrl = DEFAULT_BASE_URL, timeoutMs = 30000, jobName = 'GTS Etiket' } = options;

  if (!target.devicePath && !target.printerName) {
    throw new LocalPrintError('Etiket yazıcısı seçilmemiş.', 'NO_PRINTER');
  }

  try {
    const resp = await axios.post(
      `${baseUrl}/api/print-raw`,
      {
        base64Data: toBase64(data),
        devicePath: target.devicePath,
        printerName: target.printerName,
        jobName,
      },
      { timeout: timeoutMs },
    );
    return {
      bytes: resp.data?.bytes ?? data.length,
      message: resp.data?.message || 'Etiket gönderildi.',
    };
  } catch (err) {
    throw wrapError(err);
  }
}

/** Seçili etiket yazıcısını tarayıcıda saklar */
export function saveSelectedPrinter(selection: LabelPrinterSelection | null): void {
  try {
    if (selection) localStorage.setItem(SELECTED_PRINTER_KEY, JSON.stringify(selection));
    else localStorage.removeItem(SELECTED_PRINTER_KEY);
  } catch {
    /* depolama kapalı olabilir — seçim oturumluk kalır */
  }
}

export function loadSelectedPrinter(): LabelPrinterSelection | null {
  try {
    const raw = localStorage.getItem(SELECTED_PRINTER_KEY);
    return raw ? (JSON.parse(raw) as LabelPrinterSelection) : null;
  } catch {
    return null;
  }
}
